import { Link, Navigate, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import AdSlot from '../components/AdSlot';
import SnakeGarden from '../games/SnakeGarden';
import MemoryBloom from '../games/MemoryBloom';
import Petal2048 from '../games/Petal2048';
import MinesGarden from '../games/MinesGarden';
import { getGameBySlug, getMoreGames } from '../data/games';
import { saveRecentlyPlayed, trackEvent, trackGameExit, trackGameStart } from '../utils/analytics';
import './GameDetail.css';

const gameComponents = {
  snake: SnakeGarden,
  memory: MemoryBloom,
  '2048': Petal2048,
  mines: MinesGarden,
};

const GamePlayer = ({ game }) => {
  if (game.gameType === 'iframe') {
    return (
      <iframe
        src={game.playUrl}
        title={game.name}
        className="detail-game-iframe"
        frameBorder="0"
        allowFullScreen
      />
    );
  }

  const PlayableGame = gameComponents[game.gameType];
  return PlayableGame ? <PlayableGame game={game} /> : null;
};

const GameDetail = () => {
  const { slug } = useParams();
  const game = getGameBySlug(slug);

  if (!game) {
    return <Navigate to="/games" replace />;
  }

  const moreGames = getMoreGames(game.slug);

  const handleStart = () => {
    trackGameStart(game);
    saveRecentlyPlayed(game);
  };

  const handleExit = () => {
    trackGameExit(game);
  };

  return (
    <>
      <SEO
        title={game.seoTitle}
        description={game.seoDescription}
        keywords={`${game.name}, free online game, ${game.category}, IdeaPetal Games`}
      />
      <div className="game-detail-page">
        <section className="game-detail-hero">
          <div className="container game-detail-hero-grid">
            <div>
              {game.dailyChallenge && <p className="daily-label">Daily challenge</p>}
              <p className="game-kicker">{game.category}</p>
              <h1>{game.name}</h1>
              <p className="game-lede">{game.description}</p>
              <a className="play-anchor" href="#play" onClick={handleStart}>
                Play Now
              </a>
            </div>
            <img className="game-emblem" src={game.image} alt={`${game.name} preview`} />
          </div>
        </section>

        <section className="game-detail-content">
          <div className="container game-detail-layout">
            <main>
              <section className="play-panel" id="play">
                <div className="play-panel-header">
                  <h2>Play {game.name}</h2>
                  <Link className="back-link" to="/games" onClick={handleExit}>
                    Back to games
                  </Link>
                </div>
                <GamePlayer game={game} />
              </section>

              <AdSlot label="Below-game ad space reserved" variant="banner" />

              <section className="info-panel">
                <h2>How to Play</h2>
                <ul>
                  {game.instructions.map((instruction) => (
                    <li key={instruction}>{instruction}</li>
                  ))}
                </ul>
              </section>

              <section className="info-panel">
                <h2>Questions</h2>
                {game.faq.map((item) => (
                  <div className="faq-item" key={item.question}>
                    <h3>{item.question}</h3>
                    <p>{item.answer}</p>
                  </div>
                ))}
              </section>
            </main>

            <aside className="game-detail-sidebar">
              <AdSlot label="Sidebar ad space reserved" variant="sidebar" />
              <section className="more-games-panel">
                <h2>More Games</h2>
                {moreGames.map((item) => (
                  <a
                    className="mini-game-link"
                    key={item.id}
                    href={`/games/${item.slug}#play`}
                    onClick={() =>
                      trackEvent('game_card_click', {
                        game_id: item.id,
                        game_name: item.name,
                        source: 'more_games',
                      })
                    }
                  >
                    <img src={item.image} alt="" />
                    <strong>{item.name}</strong>
                  </a>
                ))}
              </section>
            </aside>
          </div>
        </section>
      </div>
    </>
  );
};

export default GameDetail;
